import React, { useContext } from 'react';
import {
  Typography,
  Button,
  Grid,
  TextField,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers';
import { Formik } from 'formik';
import {
  RegistrationForm,
  RequestParamsUsers,
  ErrorCodes,
  ResponseErrorBodyDataValidationError
} from '../types';
import { dateLocaleByLanguage } from '../i18n';
import { SettingsContext, GlobalErrorContext } from '../context';
import validator from 'validator';
import { AsYouType } from 'libphonenumber-js';
import users from '../lib/services/users';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { errorsByData } from '../lib/services/utils';
import { Theme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: '8px'
  },
  errorSnackbar: {
    backgroundColor: theme.palette.error.dark
  }
}));

export default function(props: { setSuccess: (success: boolean) => void }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { language } = useContext(SettingsContext);
  const { setGlobalError } = useContext(GlobalErrorContext);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      // utils={DateFnsUtils}
      adapterLocale={dateLocaleByLanguage(language)}
    >
      <Typography component="h1" variant="h5" align="center">
        {t('registrationHeader')}
      </Typography>
      <Formik
        initialValues={{} as RegistrationForm}
        validate={values => {
          const errors: { [key: string]: any } = {};
          if (!values.email) {
            errors.email = t('enterValue');
          } else {
            if (!validator.isEmail(values.email)) {
              errors.email = t('emailError');
            }
          }
          if (!values.firstName) {
            errors.firstName = t('enterValue');
          }
          if (!values.password) {
            errors.password = t('enterValue');
          }
          if (!values.lastName) {
            errors.lastName = t('enterValue');
          }
          if (!values.sex) {
            errors.sex = t('enterValue');
          }
          if (values.phone) {
            const asYouType = new AsYouType();
            asYouType.input(values.phone);
            if (!asYouType.getNumber()) {
              errors.phone = t('phoneCodeError');
            }
          }
          if (!values.agreements) {
            errors.agreements = t('registrationAgreementsError');
          }
          return errors;
        }}
        onSubmit={async (values, { setErrors, setSubmitting }) => {
          const params = requestParamsByForm(values);
          if (!params) {
            return;
          }
          const response = await users(params);
          setSubmitting(false);
          if (!response.error) {
            props.setSuccess(true);
            return;
          }
          if (response.error.code !== ErrorCodes.VALIDATION_ERROR) {
            setGlobalError(response.error);
            return;
          }
          setErrors(
            errorsByData(
              response.error.data as ResponseErrorBodyDataValidationError,
              t
            )
          );
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          errors,
          isValid,
          isSubmitting,
          setFieldValue
        }) => (
          <form className={classes.form} onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  name="email"
                  variant="standard"
                  margin="normal"
                  fullWidth
                  label={t('email')}
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.email}
                  helperText={errors.email}
                />
                <TextField
                  variant="standard"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t('password')}
                  type="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.password}
                  helperText={errors.password}
                />
                <TextField
                  name="company"
                  variant="standard"
                  margin="normal"
                  fullWidth
                  label={t('company')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.company}
                  helperText={errors.company}
                />
                <TextField
                  name="profession"
                  variant="standard"
                  margin="normal"
                  fullWidth
                  label={t('profession')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.profession}
                  helperText={errors.profession}
                />
                <TextField
                  name="position"
                  variant="standard"
                  margin="normal"
                  fullWidth
                  label={t('position')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.position}
                  helperText={errors.position}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="lastName"
                  variant="standard"
                  margin="normal"
                  fullWidth
                  label={t('lastname')}
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
                <TextField
                  name="firstName"
                  variant="standard"
                  margin="normal"
                  fullWidth
                  label={t('name')}
                  required
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
                <TextField
                  name="secondName"
                  variant="standard"
                  margin="normal"
                  fullWidth
                  label={t('patronymic')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.secondName}
                  helperText={errors.secondName}
                />
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!errors.birthDay}
                >
                  <DatePicker
                    openTo="year"
                    views={['year', 'month', 'day']}
                    label={t('dateOfBirth')}
                    inputFormat="dd.MM.yyyy"
                    value={values.birthDay || null}
                    onChange={date => {
                      setFieldValue('birthDay', date);
                    }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        variant="standard"
                        margin="normal"
                        error={!!errors.birthDay}
                      />
                    )}
                    disableFuture={true}
                  />
                  {errors.birthDay && (
                    <FormHelperText>{errors.birthDay}</FormHelperText>
                  )}
                </FormControl>
                <FormControl fullWidth margin="normal" error={!!errors.sex}>
                  <InputLabel htmlFor="sex">{t('gender')}</InputLabel>
                  <Select
                    name="sex"
                    variant="standard"
                    fullWidth
                    native
                    inputProps={{
                      name: 'sex',
                      id: 'sex'
                    }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="" />
                    <option value="male">{t('genderMale')}</option>
                    <option value="female">{t('genderFemale')}</option>
                  </Select>
                  {errors.sex && <FormHelperText>{errors.sex}</FormHelperText>}
                </FormControl>
                <TextField
                  name="phone"
                  variant="standard"
                  margin="normal"
                  fullWidth
                  label={t('phone')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.phone}
                  helperText={errors.phone}
                />
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <FormControl
                fullWidth
                margin="normal"
                error={!!errors.agreements}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="medium" />}
                      checkedIcon={
                        <CheckBoxIcon color="primary" fontSize="medium" />
                      }
                    />
                  }
                  name="agreements"
                  onChange={handleChange}
                  label={
                    <Typography variant="caption">
                      {t('registrationAgreements')}(
                      <a href={`/auth/${language}/privacy`}>
                        {t('privacyPolicy')}
                      </a>
                      ,{' '}
                      <a href={`/auth/${language}/agreement`}>
                        {t('termsOfUse')}
                      </a>
                      )
                    </Typography>
                  }
                />
                {errors.agreements && (
                  <FormHelperText>{errors.agreements}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ margin: '24px 0 16px' }}
              disabled={!isValid || isSubmitting}
            >
              {t('registration')}
            </Button>
          </form>
        )}
      </Formik>
    </LocalizationProvider>
  );
}

function requestParamsByForm(
  form: RegistrationForm
): RequestParamsUsers | null {
  if (
    !form.email ||
    !form.password ||
    !form.firstName ||
    !form.lastName ||
    !form.sex ||
    !form.agreements
  ) {
    return null;
  }
  return {
    email: form.email,
    password: form.password,
    firstName: form.firstName,
    secondName: form.secondName ? form.secondName : null,
    lastName: form.lastName,
    company: form.company ? form.company : null,
    phone: form.phone ? form.phone : null,
    birthDay: form.birthDay
      ? new Date(
          Date.UTC(
            form.birthDay.getFullYear(),
            form.birthDay.getMonth(),
            form.birthDay.getDate()
          )
        ).toISOString()
      : null,
    position: form.position ? form.position : null,
    profession: form.profession ? form.profession : null,
    sex: form.sex,
    agreements: ['privacy_policy_v1', 'terms_of_use_v1']
  };
}
